<template>
  <div ref="containerEl" class="handshake-list">
    <h5 v-if="title && filteredHandshakes.length" class="section-title">{{ title }}</h5>
    <div
      v-for="(handshake, index) in filteredHandshakes"
      :key="handshake.id"
      :class="['handshake', { 'is-striped': (index + (startsStriped ? 1 : 0)) % 2 === 1 }]"
      :data-handshake-id="handshake.id"
    >
      <div v-if="handshake.initiatorId === user?.id || handshake.responderId === user?.id" class="options options-left">
        <GamMore
          :size="GamButtonSize.X_SMALL"
          :items="getMoreItems(handshake)"
          :position="GamMorePosition.RIGHT_BELOW"
          :variant="GamButtonVariant.TERTIARY"
          is-icon-white
        />
        <GamButton
          v-if="handshake.isPasserbyPending"
          :size="GamButtonSize.X_SMALL"
          :variant="GamButtonVariant.TERTIARY"
          :left-icon="GamIconName.TRASH"
          :aria-label="localize(leaveActionKey(handshake))"
          is-icon-only
          is-icon-white
          @click="removeHandshakeId = handshake.id"
        />
      </div>

      <div v-if="showPhotoShortcut(handshake) && !isUploading(handshake.id)" class="options options-right">
        <button class="upload-photo-btn" @click="triggerPhotoUpload(handshake.id)">
          <GamIcon :name="GamIconName.IMAGE" size="14px" />
        </button>
      </div>

      <div class="names">
        <div
          :class="[
            'player',
            'initiator',
            {
              'is-padded': handshake.initiatorId === user?.id || handshake.responderId === user?.id,
              'has-passerby-remove': handshake.isPasserbyPending,
            },
          ]"
        >
          <RouterLink
            v-if="handshake.initiator"
            class="name"
            :to="{
              name: RouterNameType.USERNAME,
              params: { username: handshake.initiator.username },
              query: { back: 'history' },
            }"
          >
            {{ `@${handshake.initiator.username}` }}
          </RouterLink>
          <span v-else :class="['name', 'unknown', { 'is-passerby': handshake.isPasserbyPending }]">
            {{ emptySideLabel(handshake) }}
          </span>
          <GamIcon v-if="false && handshake.initiatorImageBlobHash" :name="GamIconName.CAMERA" />
        </div>

        <span :class="['icon', { 'is-passerby': handshake.isPasserbyPending }]">
          <button
            v-if="canOpenGame(handshake)"
            type="button"
            :class="['game-suggestion-btn', { 'is-unplayed': isSuggestionLit(handshake) }]"
            :aria-label="localize('handshake.game.openSuggestion')"
            @click="openGame(handshake)"
          >
            <span class="game-suggestion-number">
              {{ recommendationOf(handshake).position != null ? `#${recommendationOf(handshake).position}` : '960' }}
            </span>
          </button>

          <GamIcon
            v-else
            class="icon"
            :name="GamIconName.HANDSHAKE"
            size="16px"
            :is-primary="handshake.isUnique"
            :is-current="handshake.isPasserbyPending"
          />
        </span>

        <div
          :class="['player', 'responder', { 'is-padded': showPhotoShortcut(handshake) && !isUploading(handshake.id) }]"
        >
          <RouterLink
            v-if="handshake.responder"
            class="name"
            :to="{
              name: RouterNameType.USERNAME,
              params: { username: handshake.responder.username },
              query: { back: 'history' },
            }"
          >
            {{ `@${handshake.responder.username}` }}
          </RouterLink>
          <span v-else :class="['name', 'unknown', { 'is-passerby': handshake.isPasserbyPending }]">
            {{ emptySideLabel(handshake) }}
          </span>

          <GamIcon v-if="false && handshake.responderImageBlobHash" :name="GamIconName.CAMERA" />
        </div>
      </div>

      <div v-if="handshake.passerbyClaimCode" class="claim-code">
        <span v-if="claimExpiryLabel(handshake)" class="expiry">{{ claimExpiryLabel(handshake) }} ・</span>
        <span class="code-label">{{ localize('handshake.passerby.codeLabel') }}</span>
        <span class="code">{{ handshake.passerbyClaimCode }}</span>
      </div>

      <!-- One row: each played position is a card with its photos above the rank, the meeting photos last. -->
      <div
        v-if="cardCount(handshake)"
        :class="['photo-cards', { 'is-peek': cardCount(handshake) > 2, 'is-single': cardCount(handshake) === 1 }]"
      >
        <button
          v-if="isUploading(handshake.id)"
          class="photo-card handshake-image-skeleton skeleton"
          :title="localize('handshake.photo.cancel')"
          @click="cancel(handshake.id)"
        >
          <GamIcon :name="GamIconName.X_CLOSE" size="14px" />
        </button>
        <div v-for="card in cardsFor(handshake)" :key="card.key" class="photo-card">
          <div v-if="showsPhotoSquares(handshake)" class="card-photo">
            <HandshakeImage v-if="card.hashes.length" :blob-hash="card.hashes[0]" @click="openCard(handshake, card)" />
            <button
              v-else-if="canAddPositionPhoto(handshake)"
              type="button"
              class="card-empty is-add"
              :aria-label="localize('handshake.photo.add')"
              @click="card.position != null && triggerPhotoUpload(handshake.id, card.position)"
            >
              <GamIcon :name="GamIconName.IMAGE" size="20px" is-current />
            </button>
            <div v-else class="card-empty" />
            <!-- More than one photo here. -->
            <span v-if="card.hashes.length > 1" class="stack-chip" aria-hidden="true" />
          </div>

          <div
            v-if="card.position != null"
            class="card-rank"
            :class="{ 'is-editable': canEditPositions(handshake) }"
            @click="canEditPositions(handshake) && openPositions(handshake)"
          >
            <HandshakePositionMark
              :position-index="card.position"
              :number-color="numberColor(handshake, card.position)"
              align="start"
              show-number
            />
            <button
              v-if="canEditPositions(handshake)"
              type="button"
              class="position-edit"
              :aria-label="localize('handshake.positions.action')"
              @click.stop="openPositions(handshake)"
            >
              <GamIcon :name="GamIconName.EDIT" size="10px" is-current />
            </button>
          </div>
        </div>
      </div>

      <!-- What the two of them said the game was. Either side may have written
           nothing, and neither has to agree. A participant can open the same
           note from the line, since a note stays editable. -->
      <component
        :is="canWriteNote(handshake) ? 'button' : 'div'"
        v-if="noteLine(handshake)"
        :type="canWriteNote(handshake) ? 'button' : undefined"
        class="played-note"
        :aria-label="canWriteNote(handshake) ? localize('handshake.note.action') : undefined"
        @click="openNote(handshake)"
      >
        {{ noteLine(handshake) }}
      </component>

      <div v-if="showTimeAndOccasion" class="time-and-occasion">
        <RouterLink
          v-if="handshake.location"
          class="place"
          :to="{
            name: RouterNameType.LOCATIONS,
            query: {
              spot: handshake.locationId,
              lat: handshake.location.coordinates.lat,
              lng: handshake.location.coordinates.lng,
              zoom: 16,
            },
          }"
        >
          <GamIcon class="place-pin" :name="GamIconName.PIN" size="12px" is-current />
          <span class="place-name">{{ handshake.location.name }}</span>
        </RouterLink>

        <span class="time">
          <template v-if="handshake.location"> ・ </template>
          {{ handshakeMonth(handshake) }}
        </span>
      </div>
    </div>

    <GamExpandImage
      v-if="selectedImage"
      v-bind="selectedImage"
      ref="imageExpand"
      show-author
      is-gallery
      is-url-bound
      :can-add="canAddGalleryPhoto"
      :add-shortcut="galleryAddShortcut"
      :can-delete="canDeleteImage"
      @close="handleImageClose"
      @delete="handleDeleteImage"
      @add="addGalleryPhoto"
      @position="openPhotoPosition"
    />

    <HandshakeNoteModal
      v-if="noteHandshake"
      :note="ownNote(noteHandshake)"
      @close="noteHandshake = null"
      @save="handleSaveNote"
    />

    <HandshakePositionsModal
      v-if="positionsHandshake"
      :handshake-id="positionsHandshake.id"
      :positions="positionsHandshake.positions || []"
      :started-positions="positionsHandshake.startedPositions || []"
      :photos="positionsHandshake.photos || []"
      :user-id="user?.id"
      @close="positionsHandshake = null"
      @change="rememberEditedPositions"
      @open-photo="openPositionPhotos"
    />

    <HandshakePhotoPositionModal
      v-if="photoPosition"
      :handshake-id="photoPosition.handshakeId"
      :positions="photoPosition.positions"
      :current="photoPosition.positionIndex"
      :taken="photoPosition.taken"
      :meeting-full="photoPosition.meetingFull"
      :image-hash="photoPosition.hash"
      @close="photoPosition = null"
      @change="rememberMovedPhoto"
    />

    <GamConfirmModal
      v-if="removeHandshakeId"
      :title="localize(removingDeletesHandshake ? 'handshake.delete.title' : 'handshake.remove.title')"
      :keep-label="localize('handshake.remove.dismiss')"
      :destructive-label="localize(removingDeletesHandshake ? 'handshake.delete.confirm' : 'handshake.remove.confirm')"
      @close="removeHandshakeId = null"
      @keep="removeHandshakeId = null"
      @confirm="handleConfirmRemoveHandshake"
    />
  </div>
</template>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { ImageDto } from '@/core/data/location/location.interface';
import { BlobSource } from '@/core/data/report/report.type';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import { formatMonthLabel } from '@/core/helpers/date.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useLiveHandshakesStore } from '@/stores/liveHandshakes.store';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamConfirmModal from '@/views/components/GamConfirmModal.vue';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import type { GamMoreItem } from '@/views/composables/models/components/GamMore';
import { pushHandshakeClock } from '@/views/main/clock/utils/open-handshake-clock';
import HandshakeImage from '@/views/main/handshakes/HandshakeImage.vue';
import HandshakeNoteModal from '@/views/main/handshakes/HandshakeNoteModal.vue';
import HandshakePhotoPositionModal from '@/views/main/handshakes/HandshakePhotoPositionModal.vue';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import HandshakePositionsModal from '@/views/main/handshakes/HandshakePositionsModal.vue';
import { rememberPhotoHandshakes } from '@/views/main/handshakes/use-position-photos';
import {
  hasPlayedPosition,
  isHandshakeLive,
  isOpenHandshake,
  passerbyDisplayName,
  type HandshakeGamePlayer,
} from '@/views/main/handshakes/utils/handshake-game';
import { handshakeNotes } from '@/views/main/handshakes/utils/handshake-note';
import {
  photoCards,
  photosInSlot,
  photosToUpload,
  positionsAtPhotoLimit,
  type PhotoCard,
} from '@/views/main/handshakes/utils/handshake-photos';
import { readAskedPositions } from '@/views/main/handshakes/utils/played-prompt';
import { collectPositions, type PositionTone } from '@/views/main/handshakes/utils/position-collection';
import { currentRecommendation, type CurrentRecommendation } from '@/views/main/handshakes/utils/recommendation-state';
import { useFileDialog, useNow } from '@vueuse/core';
import { differenceInHours } from 'date-fns';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

export interface HandshakeListProps {
  handshakes: HandshakeDto[];
  showTimeAndOccasion?: boolean;
  enableGameSuggestion?: boolean;
  /** Whose collection the position colours follow. The profile this list is on. */
  userId?: string;
  /** The first row takes the tinted band, continuing stripes that already started above the list. */
  startsStriped?: boolean;
  /** Section label rendered above the rows. */
  title?: string;
}

const router = useRouter();
const route = useRoute();
const toast = useToast();

const props = defineProps<HandshakeListProps>();
const emit = defineEmits([GamComponentsEmits.UPDATE]);

watch(() => props.handshakes, rememberPhotoHandshakes, { immediate: true });

const positionTones = computed(() => (props.userId ? collectPositions(props.handshakes, props.userId).tones : null));

/** The table's colour for this number: pink when it has its own player, white when repeated, gray when nobody. */
const numberColor = (handshake: HandshakeDto, position: number): Exclude<PositionTone, 'empty'> => {
  const tone = positionTones.value?.[position];
  if (tone && tone !== 'empty') return tone;

  return handshake.initiator?.username && handshake.responder?.username ? 'repeat' : 'unknown';
};

const imageExpand = ref<InstanceType<typeof GamExpandImage>>();
const containerEl = ref<HTMLDivElement>();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
// A row goes quiet on its own when its handshake stops being the live one.
const now = useNow({ interval: 60_000 });

const mountedAt = Date.now();

const uploadTarget = ref<{ handshakeId: string; positionIndex?: number } | null>(null);
const scrollToHandshakeId = ref<string | null>(null);

const { isUploading, upload, cancel, lastSettled } = useHandshakePhotoUpload();

const { open: openFileDialog, onChange: onFileChange } = useFileDialog({
  accept: '.jpg, .jpeg, .png',
  multiple: true,
});

/** Newest checked position first, so the one being played is the one in view. */
const latestPositions = (positions: number[] | undefined): number[] => (positions ? [...positions].reverse() : []);

type PhotoEntry = { hash: string; ownerId: string | null; positionIndex: number | null; createdAt?: string };

const photoEntries = (handshake: HandshakeDto): PhotoEntry[] => {
  if (handshake.photos) {
    return handshake.photos.map((photo) => ({
      hash: photo.imageBlobHash,
      ownerId: photo.userId,
      positionIndex: photo.positionIndex,
      createdAt: photo.createdAt,
    }));
  }

  const entries: PhotoEntry[] = [];
  if (handshake.initiatorImageBlobHash) {
    entries.push({ hash: handshake.initiatorImageBlobHash, ownerId: handshake.initiatorId, positionIndex: null });
  }
  if (handshake.responderImageBlobHash) {
    entries.push({ hash: handshake.responderImageBlobHash, ownerId: handshake.responderId, positionIndex: null });
  }
  return entries;
};

const cardsFor = (handshake: HandshakeDto): PhotoCard[] =>
  photoCards(latestPositions(handshake.positions), photoEntries(handshake));

const cardCount = (handshake: HandshakeDto): number => cardsFor(handshake).length + (isUploading(handshake.id) ? 1 : 0);

/** A position's card opens on its own photos. The meeting card opens the whole handshake. */
const openCard = (handshake: HandshakeDto, card: PhotoCard) => {
  const hash = card.hashes[0];
  if (!hash) return;

  if (card.position == null) handleOpenImage(hash, handshake.id);
  else openHandshakePhotos(hash, { handshakeId: handshake.id, positionIndex: card.position });
};

const liveStore = useLiveHandshakesStore();
if (props.enableGameSuggestion) void liveStore.ensure();

/** Both players known: the live store decides its game, the same as on a spot or gambit. A passerby one has no peer there. */
const hasPeer = (handshake: HandshakeDto): boolean => !!handshake.initiatorId && !!handshake.responderId;

const isLiveOpen = (handshake: HandshakeDto): boolean => {
  const live = liveStore.byId(handshake.id);
  return !!live && !live.closedAt && new Date(live.liveUntil).getTime() > now.value.getTime();
};

const isUnplayed = (handshake: HandshakeDto): boolean =>
  !hasPlayedPosition(hasPeer(handshake) ? liveStore.byId(handshake.id) : handshake);

/** Read from the live store when both players are known, so it moves with the other phone. */
const recommendationOf = (handshake: HandshakeDto): CurrentRecommendation => {
  if (!hasPeer(handshake)) return currentRecommendation(handshake);

  const live = liveStore.byId(handshake.id);
  return live && isLiveOpen(handshake) ? currentRecommendation(live) : { state: 'unknown', position: null };
};

/** A number still to be played is pink, and gray once a game is on it. Without one, pink until a game is saved. */
const isSuggestionLit = (handshake: HandshakeDto): boolean => {
  const recommendation = recommendationOf(handshake);
  return recommendation.position != null ? recommendation.state !== 'open' : isUnplayed(handshake);
};

const canOpenGame = (handshake: HandshakeDto): boolean => {
  if (!props.enableGameSuggestion) return false;
  if (hasPeer(handshake)) return isLiveOpen(handshake);

  return isOpenHandshake(handshake, props.handshakes, user.value?.id, now.value);
};

const openGame = (handshake: HandshakeDto) => {
  if (!canOpenGame(handshake) || !handshake.initiator) return;

  const live = liveStore.byId(handshake.id);
  const initiator: HandshakeGamePlayer = {
    id: handshake.initiator.id,
    username: handshake.initiator.username,
    role: 'initiator',
    image: handshake.initiator.profileImageBlobHash,
  };
  const responder: HandshakeGamePlayer = handshake.responder
    ? {
        id: handshake.responder.id,
        username: handshake.responder.username,
        role: 'responder',
        image: handshake.responder.profileImageBlobHash,
      }
    : {
        id: `${handshake.id}:passerby`,
        username: passerbyDisplayName(
          localize('handshake.player.passerby'),
          handshake.passerbyClaimCode,
          handshake.isPasserbyPending,
        ),
        role: 'responder',
        isPasserby: true,
      };

  pushHandshakeClock({
    handshakeId: handshake.id,
    players: [initiator, responder],
    round: live?.suggestionRound ?? handshake.suggestionRound,
    suggestionSeed: live?.suggestionSeed ?? handshake.suggestionSeed,
    initiatorPlaysWhite: live?.suggestionInitiatorWhite ?? handshake.suggestionInitiatorWhite,
    minutes: live?.suggestionMinutes ?? handshake.suggestionMinutes,
    increment: live?.suggestionIncrement ?? handshake.suggestionIncrement,
    clockChosen: live?.suggestionClockChosen ?? handshake.suggestionClockChosen,
  });
};

onFileChange(async (files: FileList | null) => {
  if (!files?.length || !uploadTarget.value || !user.value) return;
  const target = uploadTarget.value;
  uploadTarget.value = null;

  const handshake = props.handshakes.find((item) => item.id === target.handshakeId);
  const position = target.positionIndex ?? null;
  const already = photosInSlot(handshake ?? {}, user.value.id, position);
  const batch = photosToUpload(files, already);
  if (!batch.length || batch.length < files.length) {
    toast.error(localize(position == null ? 'handshake.photo.limitHandshake' : 'handshake.photo.taken'));
  }
  if (!batch.length) return;

  await upload(target.handshakeId, batch, target.positionIndex);
});

// A photo that landed did so on its own time, and may well have been sent from
// a screen that is no longer here — whichever list is up asks for it now.
watch(lastSettled, (settled) => {
  if (!settled) return;
  scrollToHandshakeId.value = settled.handshakeId;
  emit(GamComponentsEmits.UPDATE);
});

watch(
  () => props.handshakes,
  async () => {
    if (!scrollToHandshakeId.value) return;
    const targetId = scrollToHandshakeId.value;
    scrollToHandshakeId.value = null;
    await nextTick();
    containerEl.value?.querySelector(`[data-handshake-id="${targetId}"]`)?.scrollIntoView({ block: 'nearest' });
  },
);

const canAddPhoto = (handshake: HandshakeDto): boolean => {
  if (!user.value) return false;
  const isInitiator = handshake.initiatorId === user.value.id;
  const isResponder = handshake.responderId === user.value.id;
  if (!isInitiator && !isResponder) return false;
  // The outside button offers the meeting photo. A position's own photo is added on its card.
  return photosInSlot(handshake, user.value.id, null) < 5;
};

/** A played position with no photo yet is a way to add one, for either player, while the handshake is live. */
const canAddPositionPhoto = (handshake: HandshakeDto): boolean =>
  !!user.value &&
  (handshake.initiatorId === user.value.id || handshake.responderId === user.value.id) &&
  isOpenHandshake(handshake, props.handshakes, user.value.id, now.value);

/**
 * The squares above the ranks. Once the handshake is over with no photo on it,
 * there is nothing to line up with and nothing to add, so only the ranks stay.
 */
const showsPhotoSquares = (handshake: HandshakeDto): boolean =>
  isUploading(handshake.id) ||
  cardsFor(handshake).some((card) => card.hashes.length) ||
  isOpenHandshake(handshake, props.handshakes, user.value?.id, now.value);

/** The outside photo button. Only the handshake that is still the live one. */
const showPhotoShortcut = (handshake: HandshakeDto): boolean =>
  canAddPhoto(handshake) && isOpenHandshake(handshake, props.handshakes, user.value?.id, now.value);

/** A note goes on your own side, so it is the two in the handshake who have one. */
const canWriteNote = (handshake: HandshakeDto): boolean =>
  !!user.value && (handshake.initiatorId === user.value.id || handshake.responderId === user.value.id);

/** The word this player wrote, which is the one the row takes them back to. */
const ownNote = (handshake: HandshakeDto): string =>
  (handshake.initiatorId === user.value?.id ? handshake.initiatorNote : handshake.responderNote) || '';

/** Both sides' words on one line, or nothing at all where neither said any. */
const noteLine = (handshake: HandshakeDto): string | null => {
  const notes = handshakeNotes(handshake);
  if (!notes.length) return null;

  return notes.length === 1
    ? localizeWithValues('handshake.note.one', notes)
    : localizeWithValues('handshake.note.both', notes);
};

const noteHandshake = ref<HandshakeDto | null>(null);
const positionsHandshake = ref<HandshakeDto | null>(null);

/** Unchecking belongs to the open recommendation, the same six hours a position can be checked. */
const canEditPositions = (handshake: HandshakeDto): boolean =>
  !!user.value &&
  (handshake.initiatorId === user.value.id || handshake.responderId === user.value.id) &&
  isHandshakeLive(handshake.createdAt, now.value) &&
  (!!handshake.positions?.length ||
    !!handshake.startedPositions?.length ||
    readAskedPositions(handshake.id).length > 0);

const openNote = (handshake: HandshakeDto) => {
  if (canWriteNote(handshake)) noteHandshake.value = handshake;
};

const openPositions = (handshake: HandshakeDto) => {
  if (canEditPositions(handshake)) positionsHandshake.value = handshake;
};

const rememberEditedPositions = (positions: number[]) => {
  const handshake = positionsHandshake.value;
  if (handshake) handshake.positions = positions;
};

const photoPosition = ref<{
  handshakeId: string;
  hash: string;
  ownerId: string | null;
  positionIndex: number | null;
  positions: number[];
  taken: number[];
  meetingFull: boolean;
} | null>(null);

watch(
  () => props.handshakes,
  (list) => {
    const open = positionsHandshake.value;
    if (!open) return;
    const fresh = list.find((item) => item.id === open.id);
    if (fresh) positionsHandshake.value = fresh;
  },
);

const openPhotoPosition = () => {
  const image = galleryImage.value;
  if (!image?.handshakeId || !image.canAssignPosition) return;

  const handshake = filteredHandshakes.value.find((item) => item.id === image.handshakeId);
  if (!handshake) return;

  const ownerId = image.user?.id ?? null;
  photoPosition.value = {
    handshakeId: handshake.id,
    hash: image.imageBlobHash,
    ownerId,
    positionIndex: image.positionIndex ?? null,
    positions: handshake.positions ?? [],
    taken: positionsAtPhotoLimit(handshake.photos ?? [], ownerId, image.imageBlobHash),
    meetingFull: photosInSlot(handshake, ownerId, null, image.imageBlobHash) >= 5,
  };
};

const rememberMovedPhoto = (positionIndex: number | null) => {
  const target = photoPosition.value;
  if (!target) return;

  const handshake = props.handshakes.find((item) => item.id === target.handshakeId);
  const photo = handshake?.photos?.find((item) => item.imageBlobHash === target.hash && item.userId === target.ownerId);
  if (photo) photo.positionIndex = positionIndex;

  const shown = selectedImage.value?.images.find((item) => item.imageBlobHash === target.hash);
  if (shown) {
    shown.positionIndex = positionIndex;
    if (handshake) shown.positionTone = positionIndex == null ? 'unknown' : numberColor(handshake, positionIndex);
  }

  photoPosition.value = {
    ...target,
    positionIndex,
    taken: positionsAtPhotoLimit(handshake?.photos ?? [], target.ownerId, target.hash),
    meetingFull: photosInSlot(handshake ?? {}, target.ownerId, null, target.hash) >= 5,
  };
};

const handleSaveNote = async (note: string) => {
  const handshake = noteHandshake.value;
  noteHandshake.value = null;
  if (!handshake) return;

  const result = await gambits.userService.setHandshakeNote(handshake.id, note);

  if (result.error) {
    toast.error(result.error.message || localize('handshake.note.failed'));
    return;
  }

  emit(GamComponentsEmits.UPDATE);
};

const triggerPhotoUpload = (handshakeId: string, positionIndex?: number) => {
  uploadTarget.value = positionIndex == null ? { handshakeId } : { handshakeId, positionIndex };
  openFileDialog({ multiple: true });
};

const removeHandshakeId = ref<string | null>(null);

/**
 * With nobody on the other side, leaving takes the whole handshake along rather
 * than only your name off it — which is what the menu has to say out loud.
 */
const isLastOneIn = (handshake: HandshakeDto): boolean => !handshake.initiatorId || !handshake.responderId;

const leaveActionKey = (handshake: HandshakeDto): string =>
  isLastOneIn(handshake) ? 'handshake.delete.action' : 'handshake.remove.action';

const removingHandshake = computed(
  () => props.handshakes.find((handshake) => handshake.id === removeHandshakeId.value) ?? null,
);

const removingDeletesHandshake = computed(() => !!removingHandshake.value && isLastOneIn(removingHandshake.value));

const getMoreItems = (handshake: HandshakeDto): GamMoreItem[] => {
  const items: GamMoreItem[] = [];

  if (handshake.passerbyClaimCode) {
    items.push({
      label: 'handshake.passerby.copy',
      onClick: () => copyClaimCode(handshake),
    });
  }

  // Saying what was played is an afterthought as often as not — the game is
  // remembered on the way home — so the row it happened on is a way back to it.
  // One name for it either way: what the menu opens does not change with
  // whether there is already a word there.
  if (canWriteNote(handshake)) {
    items.push({
      label: 'handshake.note.action',
      onClick: () => openNote(handshake),
    });
  }

  if (canEditPositions(handshake)) {
    items.push({
      label: 'handshake.positions.action',
      onClick: () => openPositions(handshake),
    });
  }

  if (canAddPhoto(handshake)) {
    items.push({
      label: 'handshake.photo.add',
      onClick: () => triggerPhotoUpload(handshake.id),
    });
  }

  items.push({
    label: leaveActionKey(handshake),
    onClick: () => {
      removeHandshakeId.value = handshake.id;
    },
  });

  return items;
};

/** The side nobody has claimed: the name it was marked unique with, else what it is. */
const emptySideLabel = (handshake: HandshakeDto): string =>
  handshake.standInName ||
  localize(handshake.isPasserbyPending ? 'handshake.player.passerby' : 'handshake.player.unknown');

const claimExpiryLabel = (handshake: HandshakeDto): string | null => {
  if (!handshake.passerbyClaimExpiresAt) return null;

  const hours = Math.max(0, differenceInHours(new Date(handshake.passerbyClaimExpiresAt), new Date()));

  // On the last day there is no whole day left to name, and "0 days left"
  // reads like the code has already gone.
  if (hours < 24) return localize('handshake.passerby.expirySoon');

  return localizeWithValues('handshake.passerby.expiry', [String(Math.floor(hours / 24))]);
};

const copyClaimCode = async (handshake: HandshakeDto) => {
  if (!handshake.passerbyClaimCode) return;

  try {
    await navigator.clipboard.writeText(handshake.passerbyClaimCode);
    toast.success(localize('handshake.passerby.copied'));
  } catch {
    toast.error(localize('handshake.passerby.copyFailed'));
  }
};

const handleConfirmRemoveHandshake = async () => {
  if (!removeHandshakeId.value) return;
  await gambits.userService.deleteHandshake(removeHandshakeId.value);
  removeHandshakeId.value = null;
  emit(GamComponentsEmits.UPDATE);
};

const handshakeMonth = (handshake: HandshakeDto): string =>
  formatMonthLabel(handshake.createdAt, handshake.timezone || 'UTC');

const filteredHandshakes = computed(() => {
  const handshakes = props.handshakes.filter(
    (h) => h.isConfirmed && (h.initiatorId || h.responderId || h.initiatorImageBlobHash || h.responderImageBlobHash),
  );
  handshakes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return handshakes;
});

const handshakeImages = computed(() => {
  const images: ImageDto[] = [];

  filteredHandshakes.value.forEach((handshake) => {
    photoEntries(handshake).forEach((entry, index) => {
      images.push({
        id: `${handshake.id}:${index}:${entry.hash}`,
        imageBlobHash: entry.hash,
        liked: false,
        user: entry.ownerId === handshake.initiatorId ? handshake.initiator : handshake.responder,
        createdAt: handshake.createdAt,
        timezone: handshake.timezone || 'UTC',
        handshakeId: handshake.id,
        positionIndex: entry.positionIndex,
        canAssignPosition:
          !!user.value &&
          (handshake.initiatorId === user.value.id || handshake.responderId === user.value.id) &&
          (entry.positionIndex != null || (handshake.positions?.length ?? 0) > 0),
        positionTone: entry.positionIndex == null ? 'unknown' : numberColor(handshake, entry.positionIndex),
      });
    });
  });

  return images;
});

/**
 * The photo on screen. The URL names it by its id; a bare hash is an older link,
 * and the same file can sit on two handshakes under one hash.
 */
const galleryImage = computed(() => {
  if (!selectedImage.value) return null;

  const photo = route.query.photo;
  if (typeof photo === 'string') {
    const images = selectedImage.value.images;
    const match = images.find((item) => item.id === photo) ?? images.find((item) => item.imageBlobHash === photo);
    if (match) return match;
  }

  return selectedImage.value.images[selectedImage.value.selectedIndex] ?? null;
});

/** Another photo of yours still fits beside the one open, on its position or on the meeting. */
const canAddGalleryPhoto = computed(() => {
  const image = galleryImage.value;
  if (!image?.handshakeId || !user.value) return false;

  const handshake = props.handshakes.find((item) => item.id === image.handshakeId);
  if (!handshake) return false;
  if (handshake.initiatorId !== user.value.id && handshake.responderId !== user.value.id) return false;

  return photosInSlot(handshake, user.value.id, image.positionIndex ?? null) < 5;
});

/** The image icon only while this handshake is still the open one. The menu keeps Add after it closes. */
const galleryAddShortcut = computed(() => {
  if (!canAddGalleryPhoto.value) return false;

  const handshake = props.handshakes.find((item) => item.id === galleryImage.value?.handshakeId);
  if (!handshake) return false;

  return isOpenHandshake(handshake, props.handshakes, user.value?.id, now.value);
});

const addGalleryPhoto = () => {
  const image = galleryImage.value;
  if (!image?.handshakeId) return;

  if (!canAddGalleryPhoto.value) {
    toast.error(localize(image.positionIndex == null ? 'handshake.photo.limitHandshake' : 'handshake.photo.taken'));
    return;
  }

  triggerPhotoUpload(image.handshakeId, image.positionIndex ?? undefined);
};

/**
 * The open gallery. A handshake row is every photo of that handshake.
 * A position is only the photos on that number. Empty means nothing is open.
 */
const photoScope = ref<{ handshakeId: string; positionIndex?: number } | null>(null);

const imagesInScope = (images: ImageDto[]): ImageDto[] => {
  const scope = photoScope.value;
  if (!scope) return images;

  return images.filter(
    (item) =>
      item.handshakeId === scope.handshakeId &&
      (scope.positionIndex == null || item.positionIndex === scope.positionIndex),
  );
};

watch(handshakeImages, (images) => {
  if (!selectedImage.value) return;

  const shown = imagesInScope(images);
  const photo = route.query.photo;
  const byId = typeof photo === 'string' ? shown.findIndex((item) => item.id === photo) : -1;
  const index = byId >= 0 ? byId : shown.findIndex((item) => item.imageBlobHash === photo);

  selectedImage.value = {
    ...selectedImage.value,
    images: shown,
    selectedIndex: index >= 0 ? index : selectedImage.value.selectedIndex,
  };
});

const selectedImage = ref<GamExpandImageType | null>();

const canDeleteImage = computed(() => {
  if (!user.value) return false;

  const image = galleryImage.value;
  if (!image?.handshakeId) return false;

  const handshake = filteredHandshakes.value.find((h) => h.id === image.handshakeId);
  if (!handshake) return false;

  return handshake.initiatorId === user.value.id || handshake.responderId === user.value.id;
});

const handleDeleteImage = async (hash: string) => {
  const image = galleryImage.value;
  if (!image?.handshakeId || image.imageBlobHash !== hash) return;

  const result = await gambits.userService.removeHandshakeImage(image.handshakeId, hash);
  if (result.error) {
    toast.error(result.error.message || localize('handshake.photo.deleteFailed'));
    return;
  }

  handleImageClose();
  emit(GamComponentsEmits.UPDATE);
};

/** Opening a photo pushes a history entry. Closing has to undo that, or the profile's X steps back through it. */
const pushedPhoto = ref(false);

const handleOpenImage = (blobHash: string, handshakeId: string) => openHandshakePhotos(blobHash, { handshakeId });

/** The positions list opens one position of this handshake, not the whole profile gallery. */
const openPositionPhotos = (blobHash: string, positionIndex: number) => {
  const handshake = positionsHandshake.value;
  if (handshake) openHandshakePhotos(blobHash, { handshakeId: handshake.id, positionIndex });
};

/** The gallery on one handshake, or on one of its positions, starting at this photo. */
const openHandshakePhotos = (blobHash: string, scope: { handshakeId: string; positionIndex?: number }) => {
  if (Date.now() - mountedAt < 150) return;

  photoScope.value = scope;
  const images = imagesInScope(handshakeImages.value);
  const imageIndex = images.findIndex((image) => image.imageBlobHash === blobHash);
  if (imageIndex === -1) {
    photoScope.value = null;
    return;
  }

  selectedImage.value = {
    blobSource: BlobSource.HANDSHAKE,
    selectedIndex: imageIndex,
    images,
    noFilter: true,
  };

  pushedPhoto.value = true;
  router.push({ ...route, query: { ...route.query, photo: images[imageIndex].id } });

  nextTick(() => {
    imageExpand.value?.show();
  });
};

const handleImageClose = () => {
  selectedImage.value = null;
  photoScope.value = null;
  if (pushedPhoto.value && route.query.photo) {
    pushedPhoto.value = false;
    router.back();
    return;
  }

  pushedPhoto.value = false;
  if (!route.query.photo) return;

  router.replace({ ...route, query: { ...route.query, photo: undefined } });
};

const scrollIntoView = () => {
  containerEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

defineExpose({ scrollIntoView });
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.handshake-list {
  display: flex;
  flex-direction: column;
}

.section-title {
  margin: 0;
  padding-top: var(--spacing-l);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.15em;
  color: var(--color-white-30);
  text-transform: uppercase;
  text-indent: 0.25em;
}

.names {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  // Sideways only: the game button overhangs the line above and below.
  overflow-x: clip;
  overflow-y: visible;
  flex-grow: 0;
  flex-shrink: 0;
  transform: translateY(-1.5px);

  & * {
    line-height: 21px;
  }
}

.handshake {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  flex-direction: column;
  gap: var(--spacing-md);
  padding: var(--spacing-l) 0;
  position: relative;
}

.handshake:first-child {
  border-top: none;
}

.handshake.is-striped {
  background: linear-gradient(to right, transparent, var(--color-white-3), transparent);
}

.options {
  position: absolute;
  top: 14px;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  z-index: 3;
}

/*
 * A menu opens downwards, over the next handshake — whose own buttons stand at
 * the same height as the ones the menu belongs to, and being further down the
 * page, win the tie and show through it. Standing these buttons in a layer of
 * their own is what settles the tie, and it is the row with the menu open that
 * has to be lifted rather than the menu inside it: this is the box the menu is
 * sealed into, so no height given to anything within it can reach past here.
 */
.options:has(.gam-more-container.is-more-open) {
  z-index: 20;
}

.options-left {
  left: 0;

  :deep(.gam-button-container.is-icon) {
    width: 18px !important;
    height: 18px !important;
    min-height: 18px !important;
  }

  // GamButton always renders its icon at 16px, so the glyph needs its own size.
  :deep(.svg-container) {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
    min-width: 12px !important;
  }
}

.options-right {
  right: 0;
}

.upload-photo-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: var(--color-white-5);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: var(--color-white-100);

  @include hover {
    background: var(--color-white-20);
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  transform: translateY(0.5px);
  flex-shrink: 0;
}

/* The recommendation's number, in place of the clock. Small enough for the disc.
   The names row forces a 21px line, which drops the digits below the disc's middle. */
.names .game-suggestion-number {
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  transform: translateY(-1px);
}

.game-suggestion-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  // Taller than the 21px names line. It overhangs the row padding instead of
  // growing the row, so the names stay level with the three dots.
  margin-block: calc((21px - 32px) / 2);
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: var(--color-white-70);
  cursor: pointer;
  @include raised-action(gray);

  @include hover {
    color: var(--color-white);
  }

  @include press;

  &.is-unplayed {
    color: var(--color-white);
    @include raised-action;
  }
}

/* Sits back with the name of the side nobody has claimed yet: the three shades
   read as gray while it waits, white once it is a handshake, colour when it is
   someone new. */
.icon.is-passerby {
  color: var(--color-unknown);
}

.player {
  font-weight: 500;
  font-size: 16px;
  letter-spacing: 0.03em;
  width: calc((100% - 50px) / 2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  flex-shrink: 0;
  flex-grow: 0;
}

.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

a.name {
  text-decoration: none;
  color: var(--color-white-100);

  @include hover {
    color: var(--color-pink);
  }
}

span.name.unknown {
  color: var(--color-unknown);
}

/* Still waiting for someone rather than left behind by them. */
span.name.unknown.is-passerby {
  color: var(--color-unknown);
  border-bottom: 1px dashed var(--color-unknown);
}

.claim-code {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-s);

  .code-label {
    font-size: 14px;
    color: var(--color-white-40);
    margin-right: calc(var(--spacing-s) * -0.5);
  }

  .code {
    font-family: 'Hanken Grotesk', monospace;
    font-size: 14px;
    letter-spacing: 0.08em;
    color: var(--color-white-70);
    user-select: all;
    white-space: nowrap;
  }

  .expiry {
    font-size: 13px;
    color: var(--color-white-40);
    flex-shrink: 0;
  }
}

.initiator {
  justify-content: flex-end;
}

.initiator.is-padded {
  padding-left: 2em;
}

.initiator.is-padded.has-passerby-remove {
  padding-left: calc(18px + var(--spacing-xs) + 18px + 6px);
}

.responder {
  justify-content: flex-start;
}

.responder.is-padded {
  padding-right: 2em;
}

.photo-cards {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: var(--spacing-l);
  /* Room above for the edges of a stack. */
  padding-top: var(--spacing-md);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/* One card on its own sits in the middle of the row. */
.photo-cards.is-single {
  justify-content: center;
}

.photo-card {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
  width: calc(50% - var(--spacing-l) / 2);
}

/* A sliver of the next card, so a row that scrolls reads as one. */
.photo-cards.is-peek .photo-card {
  width: calc((100% - 2 * var(--spacing-l)) / 2.15);
}

.card-photo {
  position: relative;
  aspect-ratio: 1;

  :deep(.handshake-image) {
    display: block;
    width: 100%;
    filter: saturate(0);
    border-radius: var(--radius-x-small);
  }
}

/* Two overlapping squares in a dark chip: more photos under this one. */
.stack-chip {
  position: absolute;
  top: var(--spacing-s);
  right: var(--spacing-s);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  pointer-events: none;

  /* Both squares hang from the centre, so the pair sits in the middle of the chip. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    box-sizing: border-box;
    width: 10px;
    height: 10px;
    border: 1.5px solid var(--color-white);
    border-radius: 2px;
  }

  &::before {
    opacity: 0.6;
    transform: translate(calc(-50% + 1.5px), calc(-50% - 1.5px));
  }

  &::after {
    background: rgb(20, 20, 20);
    transform: translate(calc(-50% - 1.5px), calc(-50% + 1.5px));
  }
}

/* A played position with no photo, so its rank still lines up under the others. */
.card-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  border-radius: var(--radius-x-small);
  background: var(--color-dark-600);
  color: var(--color-white-40);

  &.is-add {
    cursor: pointer;

    @include press;

    @include hover {
      color: var(--color-white);
    }
  }
}

.card-rank {
  position: relative;

  &.is-editable {
    cursor: pointer;
  }

  :deep(.HandshakePositionMark) {
    width: 100%;
    max-width: none;
  }

  :deep(.rank) {
    border-radius: var(--radius-x-small);
  }
}

.handshake-image-skeleton {
  aspect-ratio: 1;
  border-radius: var(--radius-x-small);
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
}

/*
 * What was played, under the position. Quieter than the names, between the
 * position and the place and month the row ends on.
 */
.played-note {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 14px;
  line-height: 21px;
  color: var(--color-white-60);

  &:is(button) {
    display: block;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 14px;
    line-height: 21px;
    color: var(--color-white-60);
    cursor: pointer;
  }
}

/* The walk spot's remove circle, with a pen: this rank can still be edited. */
.position-edit {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  padding: 0;
  border: 1px solid var(--color-white-15);
  border-radius: 50%;
  background: var(--color-dark-700);
  color: var(--color-white);
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: -0.4375rem;
  }

  @include hover {
    border-color: var(--color-white-40);
    color: var(--color-white);
  }
}

.time-and-occasion {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  // transform: translateY(-1.5px);
  gap: var(--spacing-s);
  color: var(--color-white-40);

  & * {
    line-height: 21px;
    color: var(--color-white-40);
    font-size: 14px;
  }

  .time {
    letter-spacing: 0.05em;
    flex-shrink: 0;
    font-size: 13px;
  }

  .place {
    text-decoration: none;
    font-weight: 500;
    letter-spacing: 0.033em;
    display: inline-flex;
    align-items: center;
    gap: 0.3em;
    min-width: 0;
    max-width: 100%;
  }

  .place-pin {
    flex-shrink: 0;
  }

  .place-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }

  @include hover-capable {
    .place:hover,
    .place:hover * {
      color: var(--color-white-100);
    }
  }
}
</style>
